import { partition, sum } from 'lodash-es';
import { type Layout, type LayoutItem } from 'react-grid-layout/legacy';
import { type Chart } from 'types/models';
import { DiagrammType } from 'utils/enum';

export const layoutPropsToSave = ['i', 'x', 'y', 'w', 'h', 'minW', 'minH', 'maxW', 'maxH'];

type LayoutTemplateAttributes = Pick<LayoutItem, 'w' | 'h' | 'minW' | 'minH' | 'maxW' | 'maxH'>;

export const layoutConfig: {
   rowHeight: number;
   columns: number;
   sizes: LayoutTemplateAttributes[];
} = {
   rowHeight: 200,
   columns: 4,
   sizes: [
      { w: 1, h: 1, minW: 1, minH: 1, maxW: 2, maxH: 1 },
      { w: 2, h: 2, minW: 2, minH: 2, maxW: 4, maxH: 2 },
   ],
};

function calculatedOccupiedRows(layouts: Layout, columns: number): number {
   let remainingSlotsInColumn = columns;
   let currentHeightOccupation = 0;

   if (layouts.length === 0) {
      return 1;
   }

   return layouts.reduce((acc, layout) => {
      if (layout.w > remainingSlotsInColumn) {
         remainingSlotsInColumn = columns - layout.w;

         acc += layout.h;
      } else if (layout.h > currentHeightOccupation) {
         acc += layout.h - currentHeightOccupation;
         remainingSlotsInColumn -= layout.w;
      } else {
         remainingSlotsInColumn -= layout.w;

         if (remainingSlotsInColumn === 0) {
            acc += 1;
            remainingSlotsInColumn = columns;
            currentHeightOccupation = 0;
         }
      }

      currentHeightOccupation = layout.h;

      return acc;
   }, 0);
}

function layoutFromChart(chart: Chart, layouts: Layout): LayoutItem {
   const isSmallSize =
      chart?.overviewTab === 'emissions' || [DiagrammType.NUMBER, DiagrammType.PERCENT].includes(chart?.type as 'NUMBER' | 'PERCENT');
   const isLargeSize = !isSmallSize;
   const _layout = layouts.find((layout) => layout?.i === chart?.id?.toString()) ?? { i: '', x: 0, y: 0, w: 0, h: 0 };
   const layout = { ..._layout };

   if (!layout?.i || layout.i === '') {
      layout.i = chart?.id?.toString();
   }
   if (isSmallSize) {
      // fill in missing values
      Object.keys(layoutConfig.sizes[0]).forEach((key) => {
         const typedKey = key as keyof LayoutTemplateAttributes;
         if (!layout[typedKey] && layoutConfig.sizes[0][typedKey]) {
            layout[typedKey] = layoutConfig.sizes[0][typedKey];
         }
      });
   } else if (isLargeSize) {
      // adjust for old layout version
      if ((layout?.w === 1 && layout?.h === 1) || (layout?.w === 2 && layout?.h === 1)) {
         const scale = 2;
         layout.w *= scale;
         layout.minW = layoutConfig.sizes[1].minW;
         layout.maxW = layoutConfig.sizes[1].maxW;
         layout.h *= scale;
         layout.minH = layoutConfig.sizes[1].minH;
         layout.maxH = layoutConfig.sizes[1].maxH;
         layout.x *= scale;
         layout.y *= scale;
      }
      // fill in missing values
      Object.keys(layoutConfig.sizes[1]).forEach((key) => {
         const typedKey = key as keyof LayoutTemplateAttributes;
         if (!layout[typedKey] && layoutConfig.sizes[1][typedKey]) {
            layout[typedKey] = layoutConfig.sizes[1][typedKey];
         }
      });
   }
   // fill in missing coordinates (required), library auto-adjusts position when x or y is 0
   layout.x = 0;

   return layout;
}

export const adjustLayouts = ({
   layouts = [],
   charts = [],
   isPredefined = false,
}: {
   layouts: Layout;
   charts: Chart[];
   isPredefined?: boolean;
}): Layout => {
   return !isPredefined
      ? charts.map((chart) => {
           const isSmallSize = [DiagrammType.NUMBER, DiagrammType.PERCENT].includes(chart?.type as 'NUMBER' | 'PERCENT');
           const isLargeSize = !isSmallSize;
           const _layout = layouts.find((layout) => layout?.i === chart?.id?.toString()) ?? { i: '', x: 0, y: 0, w: 0, h: 0 };
           const layout = { ..._layout };

           if (!layout?.i || layout.i === '') {
              layout.i = chart?.id?.toString();
           }
           if (isSmallSize) {
              // fill in missing values
              Object.keys(layoutConfig.sizes[0]).forEach((key) => {
                 const typedKey = key as keyof LayoutTemplateAttributes;
                 if (!layout[typedKey] && layoutConfig.sizes[0][typedKey]) {
                    layout[typedKey] = layoutConfig.sizes[0][typedKey];
                 }
              });
           }
           if (isLargeSize) {
              // adjust for old layout version
              if ((layout?.w === 1 && layout?.h === 1) || (layout?.w === 2 && layout?.h === 1)) {
                 const scale = 2;
                 layout.w *= scale;
                 layout.minW = layoutConfig.sizes[1].minW;
                 layout.maxW = layoutConfig.sizes[1].maxW;
                 layout.h *= scale;
                 layout.minH = layoutConfig.sizes[1].minH;
                 layout.maxH = layoutConfig.sizes[1].maxH;
                 layout.x *= scale;
                 layout.y *= scale;
              }
              // fill in missing values
              Object.keys(layoutConfig.sizes[1]).forEach((key) => {
                 const typedKey = key as keyof LayoutTemplateAttributes;
                 if (!layout[typedKey] && layoutConfig.sizes[1][typedKey]) {
                    layout[typedKey] = layoutConfig.sizes[1][typedKey];
                 }
              });
           }
           // fill in missing coordinates (required), library auto-adjusts position when x or y is 0
           layout.x = layout?.x ?? 0;
           layout.y = layout?.y ?? layouts.reduce((acc, layout) => Math.max(acc, (layout?.y ?? 0) + (layout?.h ?? 2)), 0) + 1;

           return layout;
        })
      : // create layout for predefined charts like EU-Taonomy
        charts.map((chart, index) => ({
           i: chart?.id?.toString() ?? index.toString(),
           ...([DiagrammType.NUMBER, DiagrammType.PERCENT].includes(chart?.type as 'NUMBER' | 'PERCENT')
              ? layoutConfig.sizes[0]
              : layoutConfig.sizes[1]),
           x: (index * 2) % layoutConfig.columns,
           y: 0,
        }));
};

export const adjustEmissionLayouts = ({
   layouts = [],
   charts = [],
   columns = layoutConfig.columns,
   yOffset = 0,
   xOffset = 0,
}: {
   layouts: Layout;
   charts: Chart[];
   columns?: number;
   yOffset?: number;
   xOffset?: number;
}): Layout => {
   const [staticLayouts, flexibleLayouts] = partition(layouts, (layout) => layout?.static);

   return [
      ...staticLayouts,
      ...charts.map((chart, chartIndex) => {
         const layout = layoutFromChart(chart, flexibleLayouts);
         layout.y =
            yOffset +
            calculatedOccupiedRows(
               charts.slice(0, chartIndex).map((chart) => layoutFromChart(chart, flexibleLayouts)),
               columns
            );
         layout.x += chartIndex === 0 ? xOffset : 0;

         return layout;
      }),
   ].map((layout, index, allLayouts) => {
      const otherLayoutsInSameRow = allLayouts.slice(0, index).filter((otherLayout) => otherLayout.y === layout.y);

      if (otherLayoutsInSameRow.length > 0) {
         layout.x = sum(otherLayoutsInSameRow.map((otherLayout) => otherLayout.w));
      }

      return layout;
   });
};
